/**
 * Formats qu'on accepte d'afficher tels quels. Un document reçu vient d'un
 * inconnu : le type annoncé par le message ne vaut rien, seule l'extension
 * enregistrée à la réception décide, et uniquement pour des formats qui
 * n'exécutent pas de code.
 *
 * `image/svg+xml` en est volontairement absent : un SVG est un document
 * scriptable. Le HTML aussi, évidemment.
 */
export const TYPES_AFFICHABLES: Readonly<Record<string, string>> = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.txt': 'text/plain; charset=utf-8',
};

/**
 * Documents Word : jamais servis tels quels (ils restent en téléchargement),
 * mais convertis en HTML côté API pour un aperçu affiché dans un cadre isolé.
 * L'ancien `.doc` binaire n'est pas lisible par le convertisseur.
 */
export const EXTENSIONS_WORD: readonly string[] = ['.docx'];

/** Libellés du type PostgreSQL `type_document`. */
export const LIBELLES_TYPE_DOCUMENT: Readonly<Record<string, string>> = {
  cv: 'CV',
  lettre: 'Lettre',
  charte_vierge: 'Charte à signer',
  charte_signee: 'Charte signée',
  rapport: 'Rapport',
  justificatif: 'Justificatif',
  autre: 'Autre',
};

export function extensionDe(nomFichier: string): string {
  const point = nomFichier.lastIndexOf('.');
  return point > 0 ? nomFichier.slice(point).toLowerCase() : '';
}

export type FormeApercu = 'pdf' | 'image' | 'texte' | 'word' | null;

/** Comment afficher un document, ou `null` s'il ne doit pas être affiché. */
export function formeApercu(nomFichier: string): FormeApercu {
  const extension = extensionDe(nomFichier);
  if (EXTENSIONS_WORD.includes(extension)) return 'word';
  const mime = TYPES_AFFICHABLES[extension];
  if (!mime) return null;
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  return 'texte';
}

export interface TypeServi {
  mime: string;
  /** `inline` pour un aperçu, `attachment` pour tout le reste. */
  disposition: 'inline' | 'attachment';
}

export function typeServi(nomFichier: string): TypeServi {
  const affichable = TYPES_AFFICHABLES[extensionDe(nomFichier)];
  return affichable
    ? { mime: affichable, disposition: 'inline' }
    : // Format inconnu : flux binaire opaque, que le navigateur enregistre au
      // lieu de l'interpréter.
      { mime: 'application/octet-stream', disposition: 'attachment' };
}

/** Nom de fichier utilisable dans un en-tête, sans guillemet ni saut de ligne. */
export function nomSansPiege(nomFichier: string): string {
  return nomFichier.replace(/[\\"\r\n]/g, '').replace(/[^\w .()\-+]/g, '_') || 'document';
}
